// modernWorkspaceLive — Modern Layout の「ライブ ワークスペース state」モデルと遷移
// (presentation 層・純関数).
//
// 背景 (docs/_wip/modern-layout.md §9.10、consult 2026-06-14 決定2「手動ドラッグ =
// state の直接編集」/ 第3スライス):
//   - 第2スライスまで: live state = **選択中の preset id** 1 本 (`{ presetId }`)。
//     配置は `resolveWorkspace(presetId)` で都度導出していた。
//   - 第3スライス (本ファイル): preset を「出発点」とし、ユーザーが **表示/畳み +
//     ドック幅** を手動編集できるようにする。手動編集が入った瞬間、live state は
//     **「カスタム」**(= 名前付き preset から乖離した無名の WorkspaceState) になる。
//
// ## モデル: discriminated union (preset | custom)
//   - `{ mode: 'preset', presetId }`  … クリーンな preset 選択 (= 第2スライスと同じ)。
//     配置は resolveWorkspace(presetId) で導出。
//   - `{ mode: 'custom', workspace }` … 手動編集済みの無名 state。配置はこの workspace
//     そのもの。
//
//   **手動編集 (表示トグル / 幅リサイズ) = 現 live の解決済み WorkspaceState を出発点に
//   1 つフラグ/幅を変えた WorkspaceState を作り、live を custom にする** (= preset から
//   乖離)。**名前付き preset を選び直すと preset モードへ戻り、手動編集は破棄される**
//   (§9.10 決定2、確定挙動)。
//
//   名前付き保存 (Tier2「現在の配置に名前を付けて保存」) は今スライス対象外
//   (Phase 2++、§9.10 決定3)。今回の custom は「無名のライブ編集状態」1 本のみ。

import type { ModernWorkspacePref } from '../../../infrastructure/modernWorkspacePreference.js';
import {
  type FloatPosition,
  floatPanel,
  floatRail,
  movePanel,
  moveRail,
  type PanelId,
  type RailSlot,
  resolveWorkspace,
  setAsideWidth,
  setPanelHidden,
  type WorkspacePresetId,
  type WorkspaceRegion,
  type WorkspaceState,
} from './workspaceLayout.js';

/** ライブ ワークスペース state (= preset 選択 or 手動編集済み custom)。 */
export type LiveWorkspace =
  | { readonly mode: 'preset'; readonly presetId: WorkspacePresetId }
  | { readonly mode: 'custom'; readonly workspace: WorkspaceState };

/** preset 選択の live state を作る (= クリーンな出発点)。 */
export const presetLive = (presetId: WorkspacePresetId): LiveWorkspace => ({
  mode: 'preset',
  presetId,
});

/**
 * live state から表示用 `WorkspaceState` を導出する (= 配置適用の単一経路)。
 *   - preset モード → resolveWorkspace(presetId) (= 第2スライスと同じ導出)。
 *   - custom モード → 編集済み workspace そのもの。
 */
export const resolveLive = (live: LiveWorkspace): WorkspaceState =>
  live.mode === 'preset' ? resolveWorkspace(live.presetId) : live.workspace;

/**
 * パネルの表示/非表示を手動トグルした新 live state を返す。
 * 現 live を解決した WorkspaceState を出発点に setPanelHidden を適用し、**custom 化**する
 * (= preset から乖離。canvas は setPanelHidden 側で hide 無視)。
 */
export const togglePanelHidden = (
  live: LiveWorkspace,
  id: PanelId,
  hidden: boolean,
): LiveWorkspace => ({
  mode: 'custom',
  workspace: setPanelHidden(resolveLive(live), id, hidden),
});

/**
 * aside のドック幅を手動設定した新 live state を返す (custom 化)。
 * 現 live を解決した WorkspaceState を出発点に setAsideWidth を適用する (clamp 込み)。
 */
export const setLiveAsideWidth = (
  live: LiveWorkspace,
  regions: ReadonlyArray<WorkspaceRegion>,
  widthPx: number,
): LiveWorkspace => ({
  mode: 'custom',
  workspace: setAsideWidth(resolveLive(live), regions, widthPx),
});

/**
 * パネルをドラッグで並べ替え/別 region へ移動した新 live state を返す (custom 化、第4スライス)。
 * 現 live を解決した WorkspaceState を出発点に movePanel を適用する (= 同一 region 並べ替え
 * も region 間移動も同経路)。canvas の移動 / canvas region へのドロップは movePanel 側で
 * no-op (= §9.7 死守)。no-op になっても custom 化はする (= ドラッグ操作の確定として扱う)
 * 心配は不要: movePanel が同一参照を返すなら custom workspace はその同一 state を包むだけで
 * 不変条件は保たれる。
 */
export const movePanelLive = (
  live: LiveWorkspace,
  panelId: PanelId,
  toRegion: WorkspaceRegion,
  toIndex: number,
): LiveWorkspace => ({
  mode: 'custom',
  workspace: movePanel(resolveLive(live), panelId, toRegion, toIndex),
});

/**
 * leftRail (端ドック専用レール) を指定スロットへ region 移動した新 live state を返す
 * (custom 化、第5スライス)。現 live を解決した WorkspaceState を出発点に moveRail を適用する。
 * panel 移動 (movePanelLive) とは別セマンティクス: order 内の leftRail 位置だけを変え、
 * レールは常にスリムのまま (panels/hidden/widths 不変)。canvas は中央維持 (moveRail が保証)。
 * §12 (floating-panels-2026-08-01.md、方針変更 2026-08-01): leftRail がフロート中だった
 * 場合の re-dock もこの経路が担う (= moveRail 自体が floating→docked を吸収する設計、
 * floatRailLive の対)。
 */
export const moveRailLive = (live: LiveWorkspace, slot: RailSlot): LiveWorkspace => ({
  mode: 'custom',
  workspace: moveRail(resolveLive(live), slot),
});

/**
 * leftRail (toolRail+commandCluster 一体) をフロートへ切り離す / フロート中の leftRail を
 * 新しい座標へ動かした新 live state を返す (custom 化、§12「1 ユニットの粒度」)。現 live を
 * 解決した WorkspaceState を出発点に floatRail を適用する (= detach と reposition の両方を
 * この 1 経路で扱う、floatPanelLive の rail 版)。re-dock (floating → docked) は
 * moveRailLive を使う (= moveRail が floating 側の後始末も担うため、movePanelLive/floatPanelLive
 * の対称性と同じ設計)。
 */
export const floatRailLive = (live: LiveWorkspace, position: FloatPosition): LiveWorkspace => ({
  mode: 'custom',
  workspace: floatRail(resolveLive(live), position),
});

/**
 * パネルをフロートへ切り離す / フロート中パネルを新しい座標へ動かした新 live state を返す
 * (custom 化、S2 floating-panels-2026-08-01.md §6 決定4)。現 live を解決した WorkspaceState
 * を出発点に floatPanel を適用する (= detach と reposition の両方をこの 1 経路で扱う。
 * canvas / RAIL_PANEL_IDS へのフロート要求は floatPanel 側で no-op、§5 死守)。
 * re-dock (floating → docked) は既存の movePanelLive を使う (= movePanel が floating 側の
 * 後始末も担うため、専用の live wrapper を別途作らない)。
 */
export const floatPanelLive = (
  live: LiveWorkspace,
  panelId: PanelId,
  position: FloatPosition,
): LiveWorkspace => ({
  mode: 'custom',
  workspace: floatPanel(resolveLive(live), panelId, position),
});

/**
 * 名前付き preset を選び直す = preset モードへ戻り手動編集を破棄する (§9.10 決定2)。
 * (= setPreset の単一経路。ピッカーで preset を選んだとき・cycle のときに使う。)
 */
export const selectPreset = (presetId: WorkspacePresetId): LiveWorkspace => presetLive(presetId);

/**
 * ピッカー UI で表示する「現在値」を返す。
 *   - preset モード → その presetId。
 *   - custom モード → `'custom'` (= ピッカーに動的な「カスタム」option を出すための値)。
 * これによりピッカーは「カスタム編集中である」ことを表現できる (§9.10 決定2)。
 */
export const CUSTOM_PICKER_VALUE = 'custom' as const;
export type WorkspacePickerValue = WorkspacePresetId | typeof CUSTOM_PICKER_VALUE;

export const pickerValue = (live: LiveWorkspace): WorkspacePickerValue =>
  live.mode === 'preset' ? live.presetId : CUSTOM_PICKER_VALUE;

/** live が custom (= 手動編集済み) か。ピッカーの「カスタム」option 出し分け等に使う。 */
export const isCustom = (live: LiveWorkspace): boolean => live.mode === 'custom';

// ── 永続化ブリッジ (ModernWorkspacePref ⇄ LiveWorkspace) ──────────────────────
//
// 永続スキーマ (modernWorkspacePreference) は serializable な `ModernWorkspacePref`
// (= `{presetId}` | `{workspace}`)。UI の live モデルと相互変換する。**永続側は clean
// なときは `{presetId}` を、custom なときは full `{workspace}` を保存** (= §9.10 第3スライス
// の永続スキーマ)。

/** 永続 pref → live state。`{workspace}` は custom、`{presetId}` は preset モードへ。 */
export const liveFromPref = (pref: ModernWorkspacePref): LiveWorkspace =>
  'workspace' in pref ? { mode: 'custom', workspace: pref.workspace } : presetLive(pref.presetId);

/** live state → 永続 pref。custom は full state、preset は presetId のみを保存する。 */
export const prefFromLive = (live: LiveWorkspace): ModernWorkspacePref =>
  live.mode === 'custom' ? { workspace: live.workspace } : { presetId: live.presetId };
